import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError, notify } from '../notify';

// The account's browser (the extension logged in there checks in with the RM
// tool): is it online, and run an account job in it — update post stats,
// re-scrape flagged posts, run queued pin tasks, read followers. The browser
// picks the job up on its next check-in (≤30 s) and reports the result.
// An account that runs on a phone (Accounts → Runs on) has no browser: its
// jobs go to the phone fleet, and the bar shows what the fleet reports.
// Backend: /api/extension/status and /api/extension/commands.
const JOBS = [
  { type: 'post_stats', label: '📊 Update post stats', title: 'Views, removals and the 1-day / final (60h+) readings, then Top & pinned' },
  { type: 'rescrape_flagged', label: '🔄 Re-scrape flagged', title: 'Posts flagged for a re-read' },
  { type: 'pins', label: '📌 Run pin tasks', title: 'Pin / unpin tasks queued for this account' },
  { type: 'followers', label: '👥 Read followers', title: 'Only the account itself can see its followers' }
];
const JOB_NAME = Object.fromEntries(JOBS.map(j => [j.type, j.label.replace(/^\S+\s/, '')]));

export default function AccountBrowserBar({ accountId, username }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await axios.get(`${API}/api/extension/status`, { params: { account_ids: accountId } });
      setData(res.data || null);
    } catch (e) { /* best-effort; next poll retries */ }
  }, [accountId]);

  useEffect(() => {
    setData(null);
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Browsers stay logged in (extension 1.37+) until logged out here or in
  // the extension; an open one drops its login within ~30 s.
  const logout = async () => {
    if (!window.confirm(`Log out ${username ? 'u/' + username : 'this account'}'s browser${(cl?.logged_in_browsers || 0) > 1 ? 's' : ''} from the extension?\n\nSomeone will have to log in again in the extension before it can post or run jobs.`)) return;
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/api/extension/accounts/${accountId}/logout`);
      notify(r?.logged_out ? `Logged out (${r.logged_out} browser${r.logged_out > 1 ? 's' : ''})` : 'No browser was staying logged in', 'success');
      await load();
    } catch (err) { notifyError(err, 'Could not log out'); }
    setBusy(false);
  };

  const send = async (type) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/extension/commands`, { account_id: accountId, type });
      notify(phone ? `Sent to the phone fleet for ${username ? 'u/' + username : 'the account'}`
        : `Sent to ${username ? 'u/' + username : 'the account'}'s browser — it starts within ~30 s`, 'success');
      await load();
    } catch (err) { notifyError(err, 'Could not send'); }
    setBusy(false);
  };

  const ex = data?.executors?.[accountId];
  const phone = ex?.executor_type === 'fleet';
  const cl = data?.clients?.[accountId];
  const cmd = data?.commands?.[accountId];
  const st = cl?.state || {};
  const ago = (t) => { const m = Math.round((Date.now() - Date.parse(t)) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
  const online = !!cl?.online;
  const free = online && !st.posting && !st.job && !(cmd && cmd.status === 'queued');
  let line = null, color = '#777';
  if (cmd && JOB_NAME[cmd.type]) {
    const name = JOB_NAME[cmd.type];
    const r = cmd.result || {};
    if (phone && cmd.status === 'queued' && cmd.fleet_state === 'waiting') { line = `⏳ ${name}: waiting at the fleet${cmd.fleet_position ? ` · #${cmd.fleet_position} in line` : ''}${cmd.fleet_reason ? ` · ${cmd.fleet_reason}` : ''}`; color = '#f59e0b'; }
    else if (phone && cmd.status === 'queued') { line = `📨 ${name}: sent to the fleet ${ago(cmd.created_at)}`; color = '#f59e0b'; }
    else if (phone && cmd.status === 'cancelled') { line = `✕ ${name}: ${r.error || 'cancelled'}`; }
    else if (cmd.status === 'queued') { line = `⏳ ${name}: sent ${ago(cmd.created_at)}, waiting for the browser`; color = '#f59e0b'; }
    else if (cmd.status === 'running') { line = `🤖 ${name}: running…`; color = '#22c55e'; }
    else if (cmd.status === 'done') {
      const what = Number.isFinite(r.scraped) ? ` — ${r.scraped} of ${r.total} posts read`
        : Number.isFinite(r.tasks) ? ` — ${r.tasks} task${r.tasks === 1 ? '' : 's'}${r.failed ? `, ${r.failed} failed` : ''}` : '';
      line = `✓ ${name} ${ago(cmd.finished_at || cmd.created_at)}${what}`; color = '#22c55e';
    } else if (['failed', 'expired'].includes(cmd.status)) { line = `⚠ ${name}: ${r.error || cmd.status}`; color = '#ef4444'; }
  }
  const openFor = (type) => cmd && cmd.type === type && ['queued', 'running'].includes(cmd.status);
  if (phone) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '0 0 12px', padding: '8px 12px', background: '#141414', border: '1px solid #2a2a2a', borderRadius: '8px' }}>
        <span style={{ fontSize: '12px', color: '#aaa' }}>📱 Runs on a phone · {ex.device_serial || '?'} · profile {ex.profile_serial ?? '?'}</span>
        {JOBS.map(j => (
          <button key={j.type} className="btn-secondary" disabled={busy || openFor(j.type)} title={openFor(j.type) ? 'Already with the fleet' : `${j.title} — sent to the phone fleet`}
            onClick={() => send(j.type)} style={{ fontSize: '11px', padding: '4px 10px' }}>{j.label}</button>
        ))}
        {line && <span style={{ fontSize: '11px', color }}>{line}</span>}
      </div>
    );
  }
  const dot = online ? `🟢 Browser online${cl.version ? ` · v${cl.version}` : ''}${st.posting ? ' · posting' : st.job ? ` · ${JOB_NAME[st.job] || st.job}` : ''}`
    : cl ? `⚫ Browser offline · seen ${ago(cl.last_seen_at)}` : '⚫ No browser has checked in for this account';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '0 0 12px', padding: '8px 12px', background: '#141414', border: '1px solid #2a2a2a', borderRadius: '8px' }}>
      <span style={{ fontSize: '12px', color: online ? '#aaa' : '#666' }}>{dot}</span>
      {cl?.stays_logged_in && (
        <span title="The extension logs itself back in after the browser restarts (until logged out)" style={{ fontSize: '11px', color: '#888' }}>🔑 stays logged in</span>
      )}
      {JOBS.map(j => (
        <button key={j.type} className="btn-secondary" disabled={!free || busy} title={online ? j.title : 'Open the account\'s Dolphin / AdsPower profile with the extension logged in'}
          onClick={() => send(j.type)} style={{ fontSize: '11px', padding: '4px 10px' }}>{j.label}</button>
      ))}
      {line && <span style={{ fontSize: '11px', color }}>{line}</span>}
      {(cl?.logged_in_browsers || 0) > 0 && (
        <button className="btn-secondary" disabled={busy || (online && (st.posting || !!st.job))} onClick={logout}
          title={online && (st.posting || st.job) ? 'Not while it is posting or running a job' : "The extension in this account's browser stops staying logged in"}
          style={{ fontSize: '11px', padding: '4px 10px', marginLeft: 'auto' }}>Log out</button>
      )}
    </div>
  );
}
